import { cn, type MaskType, masks } from '@quadralize/utils'
import { cva } from 'class-variance-authority'
import * as React from 'react'
import type { Control, FieldValues, Path } from 'react-hook-form'
import { Checkbox } from '../checkbox/checkbox'
import { Combobox } from '../combobox/combobox'
import type { ComboboxProps } from '../combobox/combobox.types'
import { DatePicker } from '../date-picker/date-picker'
import { DateRangePicker, type DateRangePickerProps } from '../date-range-picker/date-range-picker'
import {
  FileUpload,
  FileUploadContent,
  FileUploadItem,
  FileUploadTrigger,
} from '../file-upload/file-upload'
import { Input, type InputProps } from '../input/input'
import { PasswordInput } from '../input/password-input'
import { QuantityInput } from '../input/quantity-input'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../input-otp/input-otp'
import { RadioGroup, RadioGroupItem } from '../radio-group/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select/select'
import { Slider } from '../slider/slider'
import { Switch } from '../switch/switch'
import { Textarea, type TextareaProps } from '../textarea/textarea'
import { TimePicker } from '../time-picker/time-picker'
import { FormControl, FormField, FormItem, FormLabel, useFormField } from './form'

// Helper to expand hit area without nesting interactive elements inside labels
const FormOverlay = () => {
  const { formItemId } = useFormField()
  // biome-ignore lint/a11y/noLabelWithoutControl: Label is not a control
  return <label htmlFor={formItemId} className="absolute inset-0 z-10 cursor-pointer opacity-0" />
}

// Generic props for all form fields
interface BaseFormFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label?: string
  description?: string
  className?: string
  required?: boolean
  helperText?: React.ReactNode
}

// Helper to get appropriate keyboard props based on mask or type
function getKeyboardProps(mask?: MaskType, type?: string) {
  if (mask === 'phone') {
    return { type: 'tel', inputMode: 'tel', autoComplete: 'tel' } as const
  }
  if (mask === 'email' || type === 'email') {
    return { type: 'email', inputMode: 'email', autoComplete: 'email' } as const
  }
  if (mask === 'cpf' || mask === 'cnpj' || mask === 'cpfCnpj') {
    return { inputMode: 'numeric' } as const
  }
  if (mask === 'cep') {
    return { inputMode: 'numeric', autoComplete: 'postal-code' } as const
  }
  if (
    mask === 'currency' ||
    mask === 'currencyOptional' ||
    mask === 'decimal' ||
    mask === 'percentage'
  ) {
    return { inputMode: 'decimal' } as const
  }
  if (mask === 'date' || mask === 'time') {
    return { inputMode: 'numeric' } as const
  }
  if (type === 'number') {
    return { inputMode: 'numeric' } as const
  }
  return {}
}

function getFileUploadItemKey(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}-${file.type}`
}

// --- FormInput ---
interface FormInputProps<T extends FieldValues>
  extends BaseFormFieldProps<T>,
    Omit<InputProps, 'name'> {
  mask?: MaskType
  enableAutofill?: boolean
  isLoading?: boolean
  selectOnFocus?: boolean
  showError?: boolean
}

export function FormInput<T extends FieldValues>({
  control,
  name,
  label,
  description,
  className,
  mask,
  required,
  helperText,
  enableAutofill,
  isLoading,
  selectOnFocus,
  showError = true,
  ...props
}: FormInputProps<T>) {
  const _handleFormat = (value: unknown) => {
    if (mask && masks[mask]) {
      const shouldKeepNumber =
        typeof value === 'number' &&
        (mask === 'currency' || mask === 'currencyOptional' || mask === 'decimal')
      const valueToFormat = shouldKeepNumber ? value : String(value ?? '')
      return masks[mask].format(valueToFormat as never)
    }
    return String(value ?? '')
  }

  const _handleParse = (value: string) => {
    if (mask && masks[mask]) {
      const maskObj = masks[mask] as { parse: (val: string) => string }
      return maskObj.parse(value)
    }
    return value
  }

  const keyboardProps = getKeyboardProps(mask, props.type)

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { value, onChange, ...field }, fieldState }) => {
        // Read the error unconditionally: react-hook-form only subscribes this Controller to
        // `errors` when it is read during render, so a short-circuit on `showError` would leave
        // its snapshot stale and hide the first submit's error.
        const fieldError = fieldState.error
        return (
          <FormItem className={className}>
            <FormControl>
              <Input
                {...keyboardProps}
                {...props}
                {...field}
                label={label}
                required={required}
                error={showError && !!fieldError}
                enableAutofill={enableAutofill}
                isLoading={isLoading}
                selectOnFocus={selectOnFocus}
                value={_handleFormat(value)}
                onChange={(e) => {
                  const parsed = _handleParse(e.target.value)
                  onChange(parsed)
                  props.onChange?.(e)
                }}
                helperText={(showError && fieldError?.message) || helperText || description}
              />
            </FormControl>
          </FormItem>
        )
      }}
    />
  )
}

// --- FormPasswordInput ---
interface FormPasswordInputProps<T extends FieldValues>
  extends BaseFormFieldProps<T>,
    Omit<InputProps, 'name'> {
  labelRight?: React.ReactNode
  enableAutofill?: boolean
  autoComplete?: 'current-password' | 'new-password'
}

export function FormPasswordInput<T extends FieldValues>({
  control,
  name,
  label,
  labelRight,
  description,
  className,
  required,
  helperText,
  enableAutofill,
  autoComplete = 'current-password',
  ...props
}: FormPasswordInputProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormControl>
            <PasswordInput
              autoComplete={autoComplete}
              {...props}
              {...field}
              label={label}
              labelRight={labelRight}
              required={required}
              error={!!fieldState.error}
              enableAutofill={enableAutofill}
              helperText={fieldState.error?.message || helperText || description}
            />
          </FormControl>
        </FormItem>
      )}
    />
  )
}

// --- FormTextarea ---
interface FormTextareaProps<T extends FieldValues>
  extends BaseFormFieldProps<T>,
    Omit<TextareaProps, 'name'> {}

export function FormTextarea<T extends FieldValues>({
  control,
  name,
  label,
  description,
  className,
  required,
  helperText,
  ...props
}: FormTextareaProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormControl>
            <Textarea
              {...props}
              {...field}
              value={field.value ?? ''}
              label={label}
              required={required}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || helperText || description}
            />
          </FormControl>
        </FormItem>
      )}
    />
  )
}

// --- FormSelect ---
interface FormSelectProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  placeholder?: string
  options: { label: React.ReactNode; value: string }[]
  disabled?: boolean
  clearable?: boolean
}

export function FormSelect<T extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder,
  options,
  disabled,
  clearable,
  required,
  className,
  helperText,
}: FormSelectProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormControl>
            <Select
              onValueChange={field.onChange}
              value={field.value ?? undefined}
              disabled={disabled}
              label={label}
              required={required}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || helperText || description}
            >
              <SelectTrigger
                ref={field.ref}
                onClear={clearable ? () => field.onChange('') : undefined}
                error={!!fieldState.error}
              >
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
        </FormItem>
      )}
    />
  )
}

// --- FormCombobox ---
interface FormComboboxProps<T extends FieldValues>
  extends BaseFormFieldProps<T>,
    Omit<ComboboxProps, 'value' | 'onValueChange'> {}

export function FormCombobox<T extends FieldValues>({
  control,
  name,
  label,
  description,
  className,
  required,
  helperText,
  ...props
}: FormComboboxProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormControl>
            <Combobox
              {...props}
              value={field.value}
              onValueChange={field.onChange}
              label={label}
              required={required}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || helperText || description}
            />
          </FormControl>
        </FormItem>
      )}
    />
  )
}

// --- FormSwitch ---
interface FormSwitchProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  disabled?: boolean
}

export function FormSwitch<T extends FieldValues>({
  control,
  name,
  label,
  description,
  className,
  disabled,
  required,
  helperText,
}: FormSwitchProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className="relative">
          <div
            className={cva(
              'flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm hover:bg-bg-subtle transition-colors w-full',
            )({ className })}
          >
            <div className="flex flex-col gap-0.5 flex-1 pr-4">
              {label && (
                <FormLabel required={required} className="text-base font-medium">
                  {label}
                </FormLabel>
              )}
            </div>
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={disabled}
                helperText={fieldState.error?.message || helperText || description}
              />
            </FormControl>
          </div>
          <FormOverlay />
        </FormItem>
      )}
    />
  )
}

// --- FormCheckbox ---
interface FormCheckboxProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  disabled?: boolean
}

export function FormCheckbox<T extends FieldValues>({
  control,
  name,
  label,
  description,
  className,
  disabled,
  required,
  helperText,
}: FormCheckboxProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className="relative">
          <div
            className={cva(
              'flex flex-row items-start gap-3 rounded-md border p-4 shadow-sm hover:bg-bg-subtle transition-colors w-full',
            )({ className })}
          >
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={disabled}
                helperText={fieldState.error?.message || helperText || description}
              />
            </FormControl>
            <div className="flex-1 flex flex-col gap-1 leading-none text-sm">
              {label && (
                <FormLabel required={required} className="font-medium">
                  {label}
                </FormLabel>
              )}
            </div>
          </div>
          <FormOverlay />
        </FormItem>
      )}
    />
  )
}

// --- FormRadioGroup ---
interface FormRadioGroupProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  options: { label: React.ReactNode; value: string }[]
  direction?: 'row' | 'col'
}

export function FormRadioGroup<T extends FieldValues>({
  control,
  name,
  label,
  description,
  options,
  direction = 'col',
  className,
  required,
  helperText,
}: FormRadioGroupProps<T>) {
  const generatedId = React.useId()

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormControl>
            <RadioGroup
              onValueChange={field.onChange}
              defaultValue={field.value}
              label={label}
              required={required}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || helperText || description}
              className={direction === 'row' ? 'flex flex-row gap-4' : 'flex flex-col gap-2'}
            >
              {options.map((option, index) => {
                const itemId = `${generatedId}-${name}-${index}`
                return (
                  <FormItem key={option.value} className="relative">
                    <div className="flex items-center gap-3 p-3 rounded-md border border-border-subtle hover:bg-bg-subtle transition-colors w-full">
                      <FormControl>
                        <RadioGroupItem value={option.value} id={itemId} />
                      </FormControl>
                      <FormLabel htmlFor={itemId} className="font-normal flex-1 text-sm m-0">
                        {option.label}
                      </FormLabel>
                    </div>
                    {/** biome-ignore lint/a11y/noLabelWithoutControl: Overlay for full-area click */}
                    <label htmlFor={itemId} className="absolute inset-0 cursor-pointer z-10" />
                  </FormItem>
                )
              })}
            </RadioGroup>
          </FormControl>
        </FormItem>
      )}
    />
  )
}

// --- FormSlider ---
interface FormSliderProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  min?: number
  max?: number
  step?: number
}

export function FormSlider<T extends FieldValues>({
  control,
  name,
  label,
  description,
  min = 0,
  max = 100,
  step = 1,
  className,
  required,
  helperText,
}: FormSliderProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormControl>
            <Slider
              min={min}
              max={max}
              step={step}
              defaultValue={[field.value]}
              onValueChange={(vals: number[]) => field.onChange(vals[0])}
              label={label}
              required={required}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || helperText || description}
            />
          </FormControl>
        </FormItem>
      )}
    />
  )
}
// --- FormDatePicker ---
interface FormDatePickerProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  placeholder?: string
  disabled?: boolean
  captionLayout?: 'label' | 'dropdown' | 'dropdown-months' | 'dropdown-years'
  editable?: boolean
  minDate?: Date
  disablePast?: boolean
  minYear?: number
  maxYear?: number
}

export function FormDatePicker<T extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder,
  className,
  disabled,
  captionLayout,
  editable,
  required,
  helperText,
  minDate,
  disablePast,
  minYear,
  maxYear,
}: FormDatePickerProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormControl>
            <DatePicker
              date={field.value}
              setDate={(date) => field.onChange(date ?? null)}
              placeholder={placeholder}
              disabled={disabled}
              captionLayout={captionLayout}
              editable={editable}
              minDate={minDate}
              disablePast={disablePast}
              minYear={minYear}
              maxYear={maxYear}
              label={label}
              required={required}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || helperText || description}
            />
          </FormControl>
        </FormItem>
      )}
    />
  )
}

// --- FormTimePicker ---
interface FormTimePickerProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  placeholder?: string
  disabled?: boolean
  editable?: boolean
  minTime?: Date
  maxTime?: Date
}

export function FormTimePicker<T extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder,
  className,
  disabled,
  editable,
  required,
  helperText,
  minTime,
  maxTime,
}: FormTimePickerProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormControl>
            <TimePicker
              date={field.value}
              setDate={field.onChange}
              placeholder={placeholder}
              disabled={disabled}
              editable={editable}
              label={label}
              required={required}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || helperText || description}
              minTime={minTime}
              maxTime={maxTime}
            />
          </FormControl>
        </FormItem>
      )}
    />
  )
}

// --- FormDateRangePicker ---
interface FormDateRangePickerProps<T extends FieldValues>
  extends BaseFormFieldProps<T>,
    Omit<DateRangePickerProps, 'date' | 'onDateChange'> {}

export function FormDateRangePicker<T extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder,
  className,
  disabled,
  required,
  helperText,
}: FormDateRangePickerProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormControl>
            <DateRangePicker
              date={field.value}
              onDateChange={field.onChange}
              placeholder={placeholder}
              disabled={disabled}
              label={label}
              required={required}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || helperText || description}
            />
          </FormControl>
        </FormItem>
      )}
    />
  )
}

// --- FormFileUpload ---
interface FormFileUploadProps<T extends FieldValues>
  extends BaseFormFieldProps<T>,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'onBlur'> {
  dropzoneOptions?: React.ComponentProps<typeof FileUpload>['dropzoneOptions']
  maxFiles?: number
  disabled?: boolean
  /** Optional custom trigger content */
  trigger?: React.ReactNode
  /** Show default file list within the field? Default: true */
  showList?: boolean
}

export function FormFileUpload<T extends FieldValues>({
  control,
  name,
  label,
  description,
  className,
  dropzoneOptions,
  maxFiles,
  disabled,
  required,
  helperText,
  trigger,
  showList = true,
  ...props
}: FormFileUploadProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          {label && <FormLabel required={required}>{label}</FormLabel>}
          <FormControl>
            <FileUpload
              value={field.value}
              onValueChange={field.onChange}
              dropzoneOptions={dropzoneOptions}
              maxFiles={maxFiles}
              disabled={disabled}
              {...props}
            >
              <FileUploadTrigger>{trigger}</FileUploadTrigger>
              {showList && (
                <FileUploadContent>
                  {Array.isArray(field.value) &&
                    (field.value as File[]).map((file) => (
                      <FileUploadItem key={getFileUploadItemKey(file)} file={file} />
                    ))}
                </FileUploadContent>
              )}
            </FileUpload>
          </FormControl>
          <p
            className={cn(
              'text-[0.8rem] text-text-tertiary font-medium',
              fieldState.error && 'text-error',
            )}
          >
            {fieldState.error?.message || helperText || description}
          </p>
        </FormItem>
      )}
    />
  )
}

// --- FormInputOTP ---
interface FormInputOTPProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  maxLength: number
  disabled?: boolean
  containerClassName?: string
}

export function FormInputOTP<T extends FieldValues>({
  control,
  name,
  label,
  description,
  className,
  required,
  helperText,
  maxLength,
  disabled,
  containerClassName,
}: FormInputOTPProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={cn('flex flex-col items-center lg:items-start gap-2', className)}>
          {label && <FormLabel required={required}>{label}</FormLabel>}
          <FormControl>
            <InputOTP
              maxLength={maxLength}
              value={field.value}
              onChange={field.onChange}
              disabled={disabled}
              containerClassName={cn('w-full', containerClassName)}
            >
              <InputOTPGroup className="w-full">
                {Array.from({ length: maxLength }).map((_, i) => (
                  <InputOTPSlot
                    // biome-ignore lint/suspicious/noArrayIndexKey: Slots are stable and based on maxLength
                    key={i}
                    index={i}
                    className="flex-1 h-14 text-xl"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </FormControl>
          <p
            className={cn(
              'text-[0.8rem] text-text-tertiary font-medium',
              fieldState.error && 'text-error',
            )}
          >
            {fieldState.error?.message || helperText || description}
          </p>
        </FormItem>
      )}
    />
  )
}

// --- FormQuantityInput ---
interface FormQuantityInputProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  min?: number
  max?: number
  disabled?: boolean
}

const DEFAULT_MAX_QUANTITY = 99999

export function FormQuantityInput<T extends FieldValues>({
  control,
  name,
  label,
  description,
  min = 1,
  max = DEFAULT_MAX_QUANTITY,
  applyMaxAttr,
  disabled,
  required,
  className,
  helperText,
}: FormQuantityInputProps<T> & { applyMaxAttr?: boolean }) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <div className="flex flex-col gap-2">
            {label && <FormLabel required={required}>{label}</FormLabel>}
            <FormControl>
              <QuantityInput
                value={field.value}
                onValueChange={field.onChange}
                min={min}
                max={max}
                applyMaxAttr={applyMaxAttr}
                disabled={disabled}
                fullWidth
              />
            </FormControl>
            <p
              className={cn(
                'text-[0.8rem] text-text-tertiary font-medium',
                fieldState.error && 'text-error',
              )}
            >
              {fieldState.error?.message || helperText || description}
            </p>
          </div>
        </FormItem>
      )}
    />
  )
}
